import { useCallback, useEffect, useState } from 'react';

import {
  type CreateHandoffPayload,
  CreateHandoffSchema,
} from '@polyai/stream/commands/handlers/handoff/create-config/schema';
import { createHandoff } from '@polyai/stream/store/reducers/handoff';
import { ConfirmationModal } from '@polyai/ui/components/molecules/ConfirmationModal';

import { useServerDispatch } from 'components/organisms/AgentStream/context/AgentStreamContext';
import { useConnectionTokens } from 'hooks/api/telephony/useConnectionTokens';
import { FormHandoffModal } from 'screens/Handoff/components/FormHandoffModal';
import * as Styled from './HandoffPopup.styled';

interface HandoffPopupProps {
  onCreate?: (data: { id: string; name: string }) => void;
  onCancel?: () => void;
}

export function HandoffPopup({ onCreate, onCancel }: HandoffPopupProps) {
  const serverDispatch = useServerDispatch();
  const [isSipModalOpen, setIsSipModalOpen] = useState(true);
  const { data: connectionTokens, isLoading } = useConnectionTokens();

  useEffect(() => {
    if (connectionTokens?.sandbox?.token) {
      setIsSipModalOpen(false);
    }
  }, [connectionTokens]);

  const handleCreate = useCallback(
    (data: CreateHandoffPayload) => {
      void serverDispatch(
        createHandoff({
          ...data,
          active: true,
          references: {
            flowSteps: {},
            topics: {},
          },
        }),
      );
      onCreate?.({ id: data.id, name: data.name });
    },
    [onCreate, serverDispatch],
  );

  if (isLoading) {
    return null;
  }

  return (
    <>
      <FormHandoffModal
        buttonText="Add"
        open={!isSipModalOpen && connectionTokens?.sandbox?.token !== undefined}
        size="md"
        streamSchema={CreateHandoffSchema}
        title="Add handoff"
        nested
        onCancelButtonClick={onCancel}
        onConfirmClicked={handleCreate}
      />

      <ConfirmationModal
        buttonText="Done"
        content={<Styled.TwilioSipCard />}
        hasCancelButton={false}
        open={isSipModalOpen && !connectionTokens?.sandbox?.token}
        size="md"
        subtitle="Generate a SIP address to connect your existing Twilio phone number to this assistant. You won't be able to use the call handoff action without it."
        title="Enable call handoff"
        nested
        onCancelButtonClick={() => onCancel?.()}
        onConfirmButtonClick={() => {
          setIsSipModalOpen(false);
          if (!connectionTokens?.sandbox?.token) {
            onCancel?.();
          }
        }}
      />
    </>
  );
}
